import type {
  CustomObject,
  CustomObjectDetail,
  CustomObjectField,
  CustomObjectMinimal,
  FieldCategory,
  GetAllCustomObjectsParams,
  SearchAllFieldsParams,
  SearchFieldsOptions,
} from '@kizen/api/custom-object';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Drop, Resource, WithRequired } from './types';
import {
  getAllCustomObjectFields,
  getCustomObjectDetail,
  getCustomObjectFields,
  getCustomObject,
  getCustomObjectCategories,
  getAllCustomObjects,
} from '@kizen/api/custom-object';
import { curry1, getResource } from './utils';

type AllCustomObjectFieldsQueryOptions<
  TData = CustomObjectField[],
  TError = DefaultError,
> = Omit<AllCustomObjectFieldsQuery<TData, TError>, 'queryFn' | 'queryKey'> & {
  params?: SearchAllFieldsParams;
};

type AllCustomObjectsQueryOptions<
  TData = CustomObjectMinimal[],
  TError = DefaultError,
> = Omit<AllCustomObjectsQuery<TData, TError>, 'queryFn' | 'queryKey'> & {
  params?: GetAllCustomObjectsParams;
};

type CustomObjectFieldsQueryOptions<
  TData = CustomObjectField[],
  TError = DefaultError,
> = Omit<CustomObjectFieldsQuery<TData, TError>, 'queryFn' | 'queryKey'> & {
  body?: SearchFieldsOptions['body'];
  params?: SearchFieldsOptions['params'];
};

type CustomObjectModelQueryOptions<
  TData = CustomObject,
  TError = DefaultError,
> = Omit<CustomObjectModelQuery<TData, TError>, 'queryFn' | 'queryKey'>;

type CustomObjectDetailQuery<
  TData = CustomObjectDetail,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    CustomObjectDetail,
    TError,
    TData,
    CustomObjectDetail,
    Readonly<[Resource<Readonly<['custom-objects', string, 'detail']>>]>
  >,
  'queryFn' | 'queryKey'
>;

type AllCustomObjectFieldsQuery<
  TData = CustomObjectField[],
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    TError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['custom-objects', string, 'fields', 'settings-search']>,
        SearchAllFieldsParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectCategoriesQuery<
  TData = FieldCategory[],
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    FieldCategory[],
    TError,
    TData,
    FieldCategory[],
    [Resource<Readonly<['custom-objects', string, 'categories']>>]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectFieldsQuery<
  TData = CustomObjectField[],
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    CustomObjectField[],
    TError,
    TData,
    CustomObjectField[],
    [
      Resource<
        Readonly<['custom-objects', string, 'fields', 'search']>,
        SearchFieldsOptions['params'],
        SearchFieldsOptions['body']
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

type CustomObjectModelQuery<
  TData = CustomObject,
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    CustomObject,
    TError,
    TData,
    CustomObject,
    [Resource<Readonly<['custom-objects', string]>>]
  >,
  'queryFn' | 'queryKey'
>;

type AllCustomObjectsQuery<
  TData = CustomObjectMinimal[],
  TError = DefaultError,
> = WithRequired<
  QueryObserverOptions<
    CustomObjectMinimal[],
    TError,
    TData,
    CustomObjectMinimal[],
    [Resource<Readonly<['custom-objects', 'settings-search']>>]
  >,
  'queryFn' | 'queryKey'
>;

export const allCustomObjectFieldsQuery = <
  TData = CustomObjectField[],
  TError = DefaultError,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options: AllCustomObjectFieldsQueryOptions<TData, TError> = {}
): AllCustomObjectFieldsQuery<TData, TError> => {
  const path = [
    'custom-objects',
    customObjectId,
    'fields',
    'settings-search',
  ] as const;
  const { params, ...queryOptions } = options;
  const resource = getResource(path, { params });

  return <AllCustomObjectFieldsQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllCustomObjectFields(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

export const allCustomObjectsQuery = <
  TData = CustomObjectMinimal[],
  TError = DefaultError,
>(
  axios: AxiosInstance,
  options: AllCustomObjectsQueryOptions
): AllCustomObjectsQuery<TData, TError> => {
  const path = ['custom-objects', 'settings-search'] as const;
  const resource = getResource(path);

  return <AllCustomObjectsQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getAllCustomObjects(axios, { params: qk.params, signal });
    },
    ...options,
  };
};

export const customObjectCategoriesQuery = <
  TData = FieldCategory[],
  TError = DefaultError,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options: Omit<CustomObjectCategoriesQuery<TData>, 'queryFn' | 'queryKey'> = {}
): CustomObjectCategoriesQuery<TData, TError> => {
  const path = ['custom-objects', customObjectId, 'categories'] as const;
  const resource = getResource(path);

  return <CustomObjectCategoriesQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectCategories(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const customObjectDetailQuery = <
  TData = CustomObjectDetail,
  TError = DefaultError,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options?: Omit<CustomObjectDetailQuery<TData>, 'queryFn' | 'queryKey'>
): CustomObjectDetailQuery<TData, TError> => {
  const path = ['custom-objects', customObjectId, 'detail'] as const;
  const resource = getResource(path);

  return <CustomObjectDetailQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectDetail(axios, qk.path[1], { signal });
    },
    ...options,
  };
};

export const customObjectFieldsQuery = <
  TData = CustomObjectField[],
  TError = DefaultError,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options: CustomObjectFieldsQueryOptions<TData, TError> = {}
): CustomObjectFieldsQuery<TData, TError> => {
  const path = ['custom-objects', customObjectId, 'fields', 'search'] as const;
  const { body, params, ...queryOptions } = options;
  const resource = getResource(path, { body, params, method: 'POST' });

  return <CustomObjectFieldsQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObjectFields(axios, qk.path[1], {
        body: qk.body,
        params: qk.params,
        signal,
      });
    },
    ...queryOptions,
  };
};

export const customObjectModelQuery = <
  TData = CustomObject,
  TError = DefaultError,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options: CustomObjectModelQueryOptions<TData, TError> = {}
): CustomObjectModelQuery<TData, TError> => {
  const path = ['custom-objects', customObjectId] as const;
  const resource = getResource(path);

  return <CustomObjectModelQuery<TData, TError>>{
    queryKey: [resource],
    queryFn: (context) => {
      const {
        signal,
        queryKey: [qk],
      } = context;

      return getCustomObject(axios, qk.path[1], {
        signal,
      });
    },
    ...options,
  };
};

class Queries {
  public allCustomObjectsQuery: <TData = CustomObjectMinimal[]>(
    ...args: Drop<Parameters<typeof allCustomObjectsQuery<TData>>>
  ) => ReturnType<typeof allCustomObjectsQuery<TData>>;
  public allCustomObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof allCustomObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof allCustomObjectFieldsQuery<TData>>;
  public customObjectCategoriesQuery: <TData = FieldCategory[]>(
    ...args: Drop<Parameters<typeof customObjectCategoriesQuery<TData>>>
  ) => ReturnType<typeof customObjectCategoriesQuery<TData>>;
  public customObjectDetailQuery: <TData = CustomObjectDetail>(
    ...args: Drop<Parameters<typeof customObjectDetailQuery<TData>>>
  ) => ReturnType<typeof customObjectDetailQuery<TData>>;
  public customObjectFieldsQuery: <TData = CustomObjectField[]>(
    ...args: Drop<Parameters<typeof customObjectFieldsQuery<TData>>>
  ) => ReturnType<typeof customObjectFieldsQuery<TData>>;
  public customObjectModelQuery: <TData = CustomObject>(
    ...args: Drop<Parameters<typeof customObjectModelQuery<TData>>>
  ) => ReturnType<typeof customObjectModelQuery<TData>>;

  constructor(axios: AxiosInstance) {
    this.allCustomObjectsQuery = curry1(allCustomObjectsQuery)(axios);
    this.allCustomObjectFieldsQuery = curry1(allCustomObjectFieldsQuery)(axios);
    this.customObjectCategoriesQuery = curry1(customObjectCategoriesQuery)(
      axios
    );
    this.customObjectDetailQuery = curry1(customObjectDetailQuery)(axios);
    this.customObjectFieldsQuery = curry1(customObjectFieldsQuery)(axios);
    this.customObjectModelQuery = curry1(customObjectModelQuery)(axios);
  }
}

export type CustomObjectQueries = Queries;

export const defCustomObjectQueries = (
  axios: AxiosInstance
): CustomObjectQueries => {
  return new Queries(axios);
};
